import React from "react";
import ReactDOM from "react-dom";
const D = window.SiteData;

// 高德地图 JS API key（矢量地图，高分屏高清）。如需更换在此处改。
const AMAP_KEY = "cd50e3fe97caca259e738260e29518fe";

// 按需加载高德 JS SDK（只加载一次，返回 window.AMap）
let amapLoading = null;
function loadAMap() {
  if (window.AMap) return Promise.resolve(window.AMap);
  if (amapLoading) return amapLoading;
  amapLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://webapi.amap.com/maps?v=2.0&key=${AMAP_KEY}`;
    s.async = true;
    s.onload = () => resolve(window.AMap);
    s.onerror = () => { amapLoading = null; reject(new Error("AMap SDK load failed")); };
    document.head.appendChild(s);
  });
  return amapLoading;
}
/* =========================================================
   珂玺资本 — News / Contact / Careers
   ========================================================= */

const { useState: useStateA, useEffect: useEffectA, useRef: useRefA } = React;

// 通用底部分页：仅当总数 > pageSize 时显示
function Pager({ page, pageSize, total, onChange }) {
  if (total <= pageSize) return null;
  const totalPages = Math.ceil(total / pageSize);
  const pages = [];
  // 1..totalPages，但中间太多时折叠成 1 … current-1 current current+1 … last
  const push = (n) => pages.push(n);
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) push(i);
  } else {
    push(1);
    if (page > 3) push("...");
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) push(i);
    if (page < totalPages - 2) push("...");
    push(totalPages);
  }
  return (
    <div className="pager">
      <button className="pager-btn" disabled={page <= 1} onClick={() => onChange(page - 1)}>‹</button>
      {pages.map((n, i) => (
        n === "..."
          ? <span className="pager-ellipsis" key={`e${i}`}>…</span>
          : <button
              key={n}
              className={`pager-btn ${n === page ? "active" : ""}`}
              onClick={() => onChange(n)}
            >{n}</button>
      ))}
      <button className="pager-btn" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>›</button>
    </div>
  );
}

// 招聘行 + 点击展开
function CareerRow({ job, lang, open, onToggle }) {
  const isZh = lang === "cn";
  const role = isZh ? job.role.cn : job.role.en;
  const loc  = isZh ? job.loc.cn  : job.loc.en;
  const type = isZh ? job.type.cn : job.type.en;
  const responsibilities = (isZh ? job.responsibilities?.cn : job.responsibilities?.en) || [];
  const skills = (isZh ? job.skills?.cn : job.skills?.en) || [];
  return (
    <React.Fragment>
      <div
        className={`case-card career-row ${open ? "expanded" : ""}`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
      >
        <div>
          <div className="name" style={{ fontSize: 22 }}>{role}</div>
          <div className="desc" style={{ marginTop: 6 }}>{loc} · {type} · {typeof job.exp === "object" ? (isZh ? job.exp.cn : job.exp.en) : job.exp}</div>
        </div>
        <div className="arr-wrap" aria-label={open ? "collapse" : "expand"}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d={open ? "M3 9l4-4 4 4" : "M3 5l4 4 4-4"} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
      {open && (
        <div className="case-detail open">
          <div className="case-detail-inner career-detail-inner">
            {responsibilities.length > 0 && (
              <div className="detail-block">
                <div className="eyebrow-mute">{isZh ? "工作职责" : "Responsibilities"}</div>
                <ul className="career-bullets">
                  {responsibilities.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
            {skills.length > 0 && (
              <div className="detail-block">
                <div className="eyebrow-mute">{isZh ? "技能要求 · 经验" : "Skills & Experience"}</div>
                <ul className="career-bullets">
                  {skills.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

function CareerRows({ jobs, lang }) {
  const [openIdx, setOpenIdx] = useStateA(null);
  return (
    <div>
      {(jobs || []).map((job, i) => (
        <CareerRow
          key={i}
          job={job}
          lang={lang}
          open={openIdx === i}
          onToggle={() => setOpenIdx(openIdx === i ? null : i)}
        />
      ))}
    </div>
  );
}

// =========================================================
// NEWS / INSIGHTS
// =========================================================
// 文件 → 可访问 URL。上传接口已返回完整地址（COS 全 URL 或 /api/files/... 相对路径），
// 直接用；仅当存的是裸 key 时才补 /api/files/ 前缀。
function fileUrl(key) {
  if (!key) return "";
  if (/^https?:\/\//.test(key) || key.startsWith("/")) return key;
  return `/api/files/${key}`;
}

function NewsPage({ lang, setRoute }) {
  const t = (keyOrCn, en) => {
    if (typeof keyOrCn === "string" && keyOrCn.indexOf(".") > 0 && window.I18N) {
      const [ns, k] = keyOrCn.split(".", 2);
      const val = window.I18N[lang] && window.I18N[lang][ns] && window.I18N[lang][ns][k];
      if (val !== undefined) return val;
    }
    return lang === "cn" ? keyOrCn : (en !== undefined ? en : keyOrCn);
  };
  const isZh = lang === "cn";
  const [news, setNews] = useStateA(null);
  const [filter, setFilter] = useStateA("all");
  const [page, setPage] = useStateA(1);
  const PAGE_SIZE = 10;

  useEffectA(() => {
    let cancelled = false;
    fetch("/api/news").then(r => r.ok ? r.json() : []).then(rows => { if (!cancelled) setNews(rows); }).catch(() => setNews([]));
    return () => { cancelled = true; };
  }, []);

  const title = (n) => isZh ? n.title_zh : n.title_en;
  const tagText = (n) => isZh ? n.tag_cn : n.tag_en;
  const open = (n) => setRoute(`news/${n.slug}`);

  const tags = React.useMemo(() => {
    const seen = new Map();
    (news || []).forEach((n) => {
      if (n.tag_cn && !seen.has(n.tag_cn)) seen.set(n.tag_cn, { id: n.tag_cn, cn: n.tag_cn, en: n.tag_en || n.tag_cn });
    });
    return [{ id: "all", cn: "全部", en: "All" }, ...seen.values()];
  }, [news]);

  const all = news || [];
  const filtered = filter === "all" ? all : all.filter(n => n.tag_cn === filter);
  useEffectA(() => { setPage(1); }, [filter]);
  const total = filtered.length;
  const start = (page - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(start, start + PAGE_SIZE);
  const goPage = (p) => { setPage(p); window.scrollTo({ top: document.querySelector(".tabs")?.offsetTop || 0, behavior: "smooth" }); };

  const lead = all[0];
  const side = all.slice(1, 4);

  return (
    <div className="page" data-screen-label="06 News">
      <section style={{ paddingTop: "calc(var(--nav-h) + 120px)", paddingBottom: 80 }}>
        <div className="container-wide">
          <div className="eyebrow" style={{ marginBottom: 24 }}>{t('aux.t001')}</div>
          <h1 className="h-display">{t('aux.t002')}</h1>
          <h1 className="h-display">
            {isZh
              ? <span style={{ color: "var(--gold)" }}>{t('aux.t059')}</span>
              : <span className="serif-en" style={{ color: "var(--gold)" }}>{t('aux.t059')}</span>}
          </h1>
          <p className="body-lg body-mute" style={{ maxWidth: 720, marginTop: 36 }}>
            {t('aux.t003')}
          </p>
        </div>
      </section>

      {news === null && <div className="container-wide" style={{ paddingBottom: 80, color: "var(--mute)" }}>{isZh ? "加载中…" : "Loading…"}</div>}

      {news !== null && (
      <section style={{ paddingBottom: 80 }} data-cms-redirect="news">
        <div className="container-wide">
          {lead && (
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 40, marginBottom: 60 }}>
            <div className="news-featured news-clickable" onClick={() => open(lead)} style={{ cursor: "pointer" }}>
              {lead.cover_key
                ? <img src={fileUrl(lead.cover_key)} alt="" style={{ width: "100%", aspectRatio: "16/9", objectFit: "cover", marginBottom: 32, border: "1px solid var(--line)" }} />
                : <PhImg kind="skyline" label={t('aux.t004')} style={{ aspectRatio: "16/9", marginBottom: 32 }} />}
              <div className="mono" style={{ color: "var(--gold)", letterSpacing: "0.12em" }}>{lead.date}  ·  {tagText(lead)}</div>
              <h3 className="h-2 news-hover-title" style={{ marginTop: 16 }}>{title(lead)}</h3>
              {(isZh ? lead.summary_zh : lead.summary_en) && (
                <p className="body-mute" style={{ marginTop: 16, maxWidth: 520, fontSize: 15 }}>{isZh ? lead.summary_zh : lead.summary_en}</p>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              <div className="eyebrow-mute">{t('aux.t007')}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {side.map((n, i) => (
                  <div key={n.slug} className="news-featured-item news-clickable" onClick={() => open(n)} style={{ cursor: "pointer", paddingBottom: 24, paddingTop: i === 0 ? 0 : 20, borderBottom: i < side.length - 1 ? "1px solid var(--line-2)" : "none" }}>
                    <div className="mono" style={{ color: "var(--gold)", fontSize: 11, letterSpacing: "0.12em" }}>{n.date}  ·  {tagText(n)}</div>
                    <div className="news-featured-title news-hover-title" style={{ fontFamily: "var(--f-serif-cn)", fontSize: 18, marginTop: 10 }}>{title(n)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          )}

          <div className="tabs">
            {tags.map(tg => (
              <button key={tg.id} className={`tab ${filter === tg.id ? "active" : ""}`} onClick={() => setFilter(tg.id)}>
                {isZh ? tg.cn : tg.en}
              </button>
            ))}
          </div>

          <div>
            {pageRows.map((n) => (
              <div className="news-row news-clickable" key={n.slug} onClick={() => open(n)} style={{ cursor: "pointer" }}>
                <div className="date">{n.date}</div>
                <div className="tag">{tagText(n)}</div>
                <div className="title news-hover-title">{title(n)}</div>
                <div className="news-row-arrow" aria-hidden>→</div>
              </div>
            ))}
            {pageRows.length === 0 && <div style={{ color: "var(--mute)", padding: "40px 0" }}>{isZh ? "暂无内容" : "No articles yet"}</div>}
          </div>
          <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={goPage} />
        </div>
      </section>
      )}
    </div>
  );
}

// =========================================================
// NEWS DETAIL — 富文本块排版
// =========================================================
function NewsBlocks({ blocks, lang, onZoom }) {
  const isZh = lang === "cn";
  const tx = (b, f) => (isZh ? b[f + "_zh"] : b[f + "_en"]) ?? b[f] ?? "";
  return (
    <div className="article-body">
      {(blocks || []).map((b, i) => {
        switch (b.type) {
          case "heading":
            return <h2 key={i} className={`article-h${b.level === 3 ? "3" : "2"}`}>{tx(b, "text")}</h2>;
          case "paragraph":
            return <p key={i} className="article-p">{tx(b, "text")}</p>;
          case "quote":
            return <blockquote key={i} className="article-quote">{tx(b, "text")}</blockquote>;
          case "center-quote":
            return (
              <div key={i} className="article-cquote">
                <span className="article-cquote-mark">"</span>
                <p>{tx(b, "text")}</p>
              </div>
            );
          case "center-caption":
            return <div key={i} className="article-ccaption">{tx(b, "text")}</div>;
          case "list":
            return (
              <ul key={i} className="article-list">
                {((isZh ? b.items_zh : b.items_en) || b.items || []).map((it, j) => <li key={j}>{it}</li>)}
              </ul>
            );
          case "image":
            if (!b.url) return null;   // 无图不渲染（避免空白占位条）
            return (
              <figure key={i} className={`article-figure ${b.align === "wide" ? "wide" : ""}`}>
                <img src={fileUrl(b.url)} alt={tx(b, "caption")} loading="lazy"
                  onClick={() => onZoom && onZoom(fileUrl(b.url))} style={{ cursor: "zoom-in" }} />
                {tx(b, "caption") && <figcaption>{tx(b, "caption")}</figcaption>}
              </figure>
            );
          case "image-pair": {
            if (!b.url && !b.url2) return null;   // 两图都空则不渲染
            const each = b.captionMode === "each";
            const cap1 = isZh ? (b.caption_zh ?? "") : (b.caption_en ?? "");
            const cap2 = isZh ? (b.caption2_zh ?? "") : (b.caption2_en ?? "");
            return (
              <figure key={i} className={`article-figure article-figure-pair ${b.align === "wide" ? "wide" : ""}`}>
                <div className="article-pair-imgs">
                  {[[b.url, b.fit, cap1], [b.url2, b.fit2, cap2]].map(([u, f, cap], k) => u ? (
                    <div key={k} className="article-pair-cell">
                      <img src={fileUrl(u)} alt="" loading="lazy"
                        className={`article-pair-img ${f === "height" ? "is-cover" : "is-contain"}`}
                        onClick={() => onZoom && onZoom(fileUrl(u))} style={{ cursor: "zoom-in" }} />
                      {each && cap && <figcaption className="article-paircap">{cap}</figcaption>}
                    </div>
                  ) : null)}
                </div>
                {!each && cap1 && <figcaption>{cap1}</figcaption>}
              </figure>
            );
          }
          case "divider":
            return <hr key={i} className="article-divider" />;
          default:
            return null;
        }
      })}
    </div>
  );
}

function NewsDetailPage({ lang, setRoute, slug }) {
  const isZh = lang === "cn";
  const [art, setArt] = useStateA(undefined); // undefined=loading, null=not found
  const [zoom, setZoom] = useStateA(null);       // 放大查看的图片 url
  const [actual, setActual] = useStateA(false);  // false=适配窗口, true=原图（可滚动）

  const closeZoom = () => { setZoom(null); setActual(false); };

  useEffectA(() => {
    if (!zoom) return;
    const onEsc = (e) => { if (e.key === "Escape") closeZoom(); };
    document.addEventListener("keydown", onEsc);
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [zoom]);

  useEffectA(() => {
    let cancelled = false;
    setArt(undefined);
    fetch(`/api/news/${encodeURIComponent(slug)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setArt(d); })
      .catch(() => { if (!cancelled) setArt(null); });
    return () => { cancelled = true; };
  }, [slug]);

  if (art === undefined) return <div className="page" style={{ paddingTop: "calc(var(--nav-h) + 140px)", paddingBottom: 120 }}><div className="container-narrow" style={{ color: "var(--mute)" }}>{isZh ? "加载中…" : "Loading…"}</div></div>;
  if (art === null) return (
    <div className="page" style={{ paddingTop: "calc(var(--nav-h) + 140px)", paddingBottom: 120 }}>
      <div className="container-narrow">
        <p className="body-mute">{isZh ? "文章不存在或已下架。" : "Article not found."}</p>
        <button className="text-link" style={{ marginTop: 24 }} onClick={() => setRoute("news")}>← {isZh ? "返回动态" : "Back to Insights"}</button>
      </div>
    </div>
  );

  const title = isZh ? art.title_zh : art.title_en;
  const summary = isZh ? art.summary_zh : art.summary_en;
  const tag = isZh ? art.tag_cn : art.tag_en;

  return (
    <div className="page" data-screen-label="06 News · Detail">
      {/* 整篇详情都是「动态新闻」内容，预览里右键给跳转提示，精确到该文章 */}
      <article data-cms-redirect="news" data-cms-redirect-id={art.id}>
        <header className="article-head">
          <div className="container-narrow">
            <button className="text-link" style={{ marginBottom: 32 }} onClick={() => setRoute("news")}>← {isZh ? "返回动态" : "Back to Insights"}</button>
            <div className="mono article-meta">{art.date}  ·  {tag}</div>
            <h1 className="article-title">{title}</h1>
            {summary && <p className="article-summary">{summary}</p>}
          </div>
        </header>
        {art.cover_key && (
          <div className={art.cover_align === "wide" ? "container-wide" : "container-narrow"}>
            <img className="article-cover" src={fileUrl(art.cover_key)} alt=""
              onClick={() => setZoom(fileUrl(art.cover_key))} style={{ cursor: "zoom-in" }} />
          </div>
        )}
        <div className="container-narrow">
          <NewsBlocks blocks={art.body} lang={lang} onZoom={setZoom} />
        </div>
        <div className="container-narrow" style={{ marginTop: 60, paddingBottom: 64 }}>
          <button className="text-link" onClick={() => setRoute("news")}>← {isZh ? "返回动态列表" : "Back to all insights"}</button>
        </div>
      </article>

      {zoom && ReactDOM.createPortal(
        // 渲染到 body：脱离 .page 的 transform，position:fixed 才相对视口
        <div className={`img-lightbox ${actual ? "is-actual" : "is-fit"}`} onClick={closeZoom}>
          <button className="img-lightbox-close" aria-label="关闭" onClick={(e) => { e.stopPropagation(); closeZoom(); }}>×</button>
          <img src={zoom} alt=""
            title={actual ? (isZh ? "点击恢复适配窗口" : "Click to fit") : (isZh ? "点击查看原图" : "Click for actual size")}
            onClick={(e) => { e.stopPropagation(); setActual((v) => !v); }} />
        </div>,
        document.body
      )}
    </div>
  );
}

// =========================================================
// CONTACT
// =========================================================
// 联系页地图：高德 JS API 2.0 矢量地图（WebGL，高分屏高清）。
// 高德坐标系为 GCJ-02，与 D.contact.geo 一致，标记精准落点。
// 坐标 / 名称从 D.contact 读取，可在后台「页面文案」配置。
function ContactMap({ lang }) {
  const isZh = lang === "cn";
  const t = (k) => (window.I18N?.[lang]?.[k.split(".")[0]]?.[k.split(".")[1]]) ?? k;
  const geo = (D.contact && D.contact.geo) || { lng: 113.947898, lat: 22.554921 };
  const name = isZh ? (D.contact.mapName?.cn || "珂玺资本 · 紫光信息港 B 座") : (D.contact.mapName?.en || "KEXI CAPITAL · Unisplendour Info Port Tower B");
  const addr = isZh ? D.contact.addr.cn : D.contact.addr.en;
  const amapUrl = `https://uri.amap.com/marker?position=${geo.lng},${geo.lat}&name=${encodeURIComponent(name)}&src=kexicapital&coordinate=gaode&callnative=0`;

  const elRef = useRefA(null);
  const mapRef = useRefA(null);

  useEffectA(() => {
    if (!elRef.current) return;
    let map = null;
    let cancelled = false;

    loadAMap().then((AMap) => {
      if (cancelled || !elRef.current) return;
      map = new AMap.Map(elRef.current, {
        center: [geo.lng, geo.lat],
        zoom: 16,
        viewMode: "2D",          // 矢量底图，高清
        lang: isZh ? "zh_cn" : "en",
        scrollWheel: false,      // 避免页面滚动时误缩放
      });
      mapRef.current = map;

      // 金色水滴标记（复用 .map-card-pin 视觉）
      const marker = new AMap.Marker({
        position: [geo.lng, geo.lat],
        anchor: "bottom-center",
        content: '<span class="map-pin-icon"><span class="map-card-pin-dot"></span><span class="map-card-pin-ring"></span></span>',
      });
      map.add(marker);

      const info = new AMap.InfoWindow({
        content: `<div style="font-size:13px;line-height:1.5"><b>${name}</b><br>${addr}</div>`,
        offset: new AMap.Pixel(0, -24),
      });
      marker.on("click", () => info.open(map, [geo.lng, geo.lat]));
    }).catch(() => { /* SDK 加载失败时静默，保留占位 */ });

    return () => {
      cancelled = true;
      if (map) { map.destroy(); mapRef.current = null; }
    };
  }, [geo.lat, geo.lng, name, addr, isZh]);

  return (
    <div>
      <div className="contact-map">
        <div className="contact-map-canvas" ref={elRef} />
      </div>
      <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between", color: "var(--mute)", fontSize: 12, fontFamily: "var(--f-mono)", letterSpacing: "0.08em" }}>
        <div>{geo.lat.toFixed(4)}° N · {geo.lng.toFixed(4)}° E</div>
        <a href={amapUrl} target="_blank" rel="noopener noreferrer" className="text-link">{t('aux.t048')}</a>
      </div>
    </div>
  );
}

function ContactPage({ lang }) {
  const t = (keyOrCn, en) => {
    if (typeof keyOrCn === "string" && keyOrCn.indexOf(".") > 0 && window.I18N) {
      const [ns, k] = keyOrCn.split(".", 2);
      const val = window.I18N[lang] && window.I18N[lang][ns] && window.I18N[lang][ns][k];
      if (val !== undefined) return val;
    }
    // fallback: 兼容旧 t(cn, en) 调用
    return lang === "cn" ? keyOrCn : (en !== undefined ? en : keyOrCn);
  };
  const [form, setForm] = useStateA({ first_name: "", company: "", email: "", phone: "", topic: "", message: "" });
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [state, setState] = useStateA({ status: "idle", error: null, refId: null });
  const [files, setFiles] = useStateA([]);   // 已选附件（可多选、可删除）
  const fileRef = useRefA(null);
  const isZh = lang === "cn";

  const MAX_FILES = 10;
  const addFiles = (picked) => {
    const incoming = Array.from(picked || []);
    setFiles((prev) => {
      // 去重（按 名+大小），并限制总数
      const merged = [...prev];
      for (const f of incoming) {
        if (!merged.some((x) => x.name === f.name && x.size === f.size)) merged.push(f);
      }
      return merged.slice(0, MAX_FILES);
    });
    if (fileRef.current) fileRef.current.value = ""; // 允许再次选同一文件
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, x) => x !== i));

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ status: "submitting", error: null, refId: null });
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("locale", lang);
      files.forEach((f) => fd.append("attachment", f));
      const res = await fetch("/api/contact", { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setState({ status: "error", error: isZh ? `请求过于频繁，请 ${data.retry_after || 60} 秒后再试` : `Too many requests; try again in ${data.retry_after || 60}s`, refId: null });
        return;
      }
      if (!res.ok) {
        const msg = data.error === "file_too_large"
          ? (isZh ? "单个附件不能超过 20 MB" : "Each attachment must be under 20 MB")
          : data.error === "too_many_files"
            ? (isZh ? "附件最多 10 个" : "Up to 10 attachments")
            : data.error === "unsupported_mime"
              ? (isZh ? "附件格式不支持" : "Unsupported attachment type")
              : (data.error || (isZh ? "提交失败" : "Submission failed"));
        setState({ status: "error", error: msg, refId: null });
        return;
      }
      setState({ status: "success", error: null, refId: data.id });
    } catch (err) {
      setState({ status: "error", error: String(err), refId: null });
    }
  };

  return (
    <div className="page" data-screen-label="08 Contact">
      <section style={{ paddingTop: "calc(var(--nav-h) + 120px)", paddingBottom: 80 }}>
        <div className="container-wide">
          <div className="eyebrow" style={{ marginBottom: 24 }}>{t('aux.t008')}</div>
          <h1 className="h-display">{t('aux.t009')}</h1>
          <h1 className="h-display">
            {lang === "cn"
              ? <span style={{ color: "var(--gold)" }}>{t('aux.t047')}</span>
              : <span className="serif-en" style={{ color: "var(--gold)" }}>{t('aux.t047')}</span>}
          </h1>
          <p className="body-lg body-mute" style={{ maxWidth: 720, marginTop: 36 }}>
            {t('aux.t010')}
          </p>
        </div>
      </section>

      <section className="section-tight">
        <div className="container-wide">
          <div className="contact-grid">
            {/* Left — info */}
            <div className="contact-info">
              <div className="contact-item">
                <div className="label">{t('aux.t011')}</div>
                <div className="value">{lang === "cn" ? D.contact.addr.cn : D.contact.addr.en}</div>
                <div className="value-en">{lang === "cn" ? D.contact.addr.en : D.contact.addr.cn}</div>
              </div>
              <div className="contact-item">
                <div className="label">{t('aux.t012')}</div>
                <div className="value">{lang === "cn" ? D.contact.person.cn : D.contact.person.en}</div>
                <div className="value-en">{t('common.t028')}</div>
              </div>
              <div className="contact-item">
                <div className="label">{t('aux.t013')}</div>
                <div className="value">{D.contact.phone}</div>
                <div className="value-en"><span>{t('aux.t014')}</span> · <span>{D.contact.wechat}</span></div>
              </div>
              <div className="contact-item">
                <div className="label">{t('aux.t015')}</div>
                <div className="value">{D.contact.email}</div>
              </div>
              <div className="contact-item">
                <div className="label">{t('aux.t016')}</div>
                <div className="value" style={{ fontSize: 18 }}>{lang === "cn" ? D.contact.hours.cn : D.contact.hours.en}</div>
              </div>
            </div>

            {/* Right — form */}
            <div style={{ background: "var(--bg-0)", border: "1px solid var(--line)", padding: 48 }}>
              {state.status === "success" ? (
                <div style={{ minHeight: 480, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
                  <div style={{ width: 80, height: 80, border: "1px solid var(--gold)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--gold)", marginBottom: 28 }}>
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                      <path d="M8 16l5 5 11-12" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </div>
                  <h3 className="h-2">{t('aux.t042')}</h3>
                  <p className="body-mute" style={{ marginTop: 20, maxWidth: 360, lineHeight: 1.7 }}>
                    {t('aux.t043')}
                  </p>
                  <div className="mono" style={{ color: "var(--gold)", marginTop: 24, letterSpacing: "0.12em", fontSize: 11 }}>
                    REF · KEXI-CT-{String(state.refId || 0).padStart(6, "0")}
                  </div>
                  <button className="text-link" style={{ marginTop: 28 }} onClick={() => {
                    setForm({ first_name: "", company: "", email: "", phone: "", topic: "", message: "" });
                    setFiles([]);
                    setState({ status: "idle", error: null, refId: null });
                  }}>
                    {t('aux.t044')}
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit}>
                  <div className="eyebrow">{t('aux.t017')}</div>
                  <h3 className="h-3" style={{ marginTop: 16, marginBottom: 32 }}>
                    {t('aux.t018')}
                  </h3>
                  {state.error && (
                    <div className="body-mute" style={{ marginBottom: 16, color: "#d66", fontSize: 13 }}>{state.error}</div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                    <div className="form-field">
                      <label>{t('aux.t019')}</label>
                      <input type="text" value={form.first_name} onChange={(e) => upd("first_name", e.target.value)} required />
                    </div>
                    <div className="form-field">
                      <label>{t('aux.t020')}</label>
                      <input type="text" value={form.company} onChange={(e) => upd("company", e.target.value)} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                    <div className="form-field">
                      <label>{t('aux.t015')}</label>
                      <input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} required />
                    </div>
                    <div className="form-field">
                      <label>{t('aux.t021')}</label>
                      <input type="tel" value={form.phone} onChange={(e) => upd("phone", e.target.value)} />
                    </div>
                  </div>
                  <div className="form-field">
                    <label>{t('aux.t022')}</label>
                    <select value={form.topic} onChange={(e) => upd("topic", e.target.value)}>
                      <option value="" style={{ background: "var(--bg-1)" }}>{t('aux.t023')}</option>
                      <option value={t('aux.t024')} style={{ background: "var(--bg-1)" }}>{t('aux.t024')}</option>
                      <option value={t('aux.t025')} style={{ background: "var(--bg-1)" }}>{t('aux.t025')}</option>
                      <option value={t('aux.t026')} style={{ background: "var(--bg-1)" }}>{t('aux.t026')}</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>{t('aux.t027')}</label>
                    <textarea value={form.message} onChange={(e) => upd("message", e.target.value)} placeholder={t('aux.t028')} rows="4" required />
                  </div>

                  {/* 附件（选填、可多选、可删除） */}
                  <div className="form-field">
                    <label>{t('aux.t045')}</label>
                    <input ref={fileRef} type="file" multiple
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip,.txt"
                      style={{ display: "none" }}
                      onChange={(e) => addFiles(e.target.files)} />
                    <div onClick={() => fileRef.current?.click()} className="upload-zone">
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span className="mono" style={{ color: "var(--gold)" }}>↑</span>
                        <span style={{ fontSize: 14, color: "var(--mute)" }}>
                          {t('aux.t046')}
                        </span>
                      </div>
                    </div>
                    {files.length > 0 && (
                      <ul className="upload-list">
                        {files.map((f, i) => (
                          <li key={f.name + f.size + i} className="upload-item">
                            <span className="upload-item-name" title={f.name}>{f.name}</span>
                            <span className="upload-item-size mono">{(f.size / 1e6).toFixed(1)} MB</span>
                            <button type="button" className="upload-item-del"
                              aria-label={isZh ? "删除" : "Remove"}
                              onClick={() => removeFile(i)}>×</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <button type="submit" className="btn" style={{ marginTop: 16 }} disabled={state.status === "submitting"}>
                    {state.status === "submitting" ? (isZh ? "提交中…" : "Submitting…") : t('aux.t029')}
                    <span className="arr"></span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="section">
        <div className="container-wide">
          <ContactMap lang={lang} />
        </div>
      </section>
    </div>
  );
}

// =========================================================
// CAREERS
// =========================================================
function CareersPage({ lang }) {
  // 招聘职位现从独立 jobs 表读（/api/jobs）；接口返回与旧 D.careers 同结构
  const [jobs, setJobs] = useStateA(null);
  useEffectA(() => {
    fetch("/api/jobs").then((r) => (r.ok ? r.json() : [])).then(setJobs).catch(() => setJobs([]));
  }, []);
  const t = (keyOrCn, en) => {
    if (typeof keyOrCn === "string" && keyOrCn.indexOf(".") > 0 && window.I18N) {
      const [ns, k] = keyOrCn.split(".", 2);
      const val = window.I18N[lang] && window.I18N[lang][ns] && window.I18N[lang][ns][k];
      if (val !== undefined) return val;
    }
    // fallback: 兼容旧 t(cn, en) 调用
    return lang === "cn" ? keyOrCn : (en !== undefined ? en : keyOrCn);
  };
  return (
    <div className="page" data-screen-label="07 Careers">
      <section style={{ paddingTop: "calc(var(--nav-h) + 120px)", paddingBottom: 80 }}>
        <div className="container-wide">
          <div className="eyebrow" style={{ marginBottom: 24 }}>{t('aux.t032')}</div>
          <h1 className="h-display">{t('aux.t033')}</h1>
          <h1 className="h-display">
            {lang === "cn"
              ? <span style={{ color: "var(--gold)" }}>{t('aux.t049')}</span>
              : <span className="serif-en" style={{ color: "var(--gold)" }}>{t('aux.t049')}</span>}
          </h1>
          <p className="body-lg body-mute" style={{ maxWidth: 720, marginTop: 36 }}>
            {t('aux.t034')}
          </p>
        </div>
      </section>

      {/* Why us */}
      <section className="section-tight">
        <div className="container-wide">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0, borderTop: "1px solid var(--line)", borderLeft: "1px solid var(--line)" }}>
            {[
              { numKey: "aux.t056", titleKey: "aux.t050", bodyKey: "aux.t051" },
              { numKey: "aux.t057", titleKey: "aux.t052", bodyKey: "aux.t053" },
              { numKey: "aux.t058", titleKey: "aux.t054", bodyKey: "aux.t055" },
            ].map((w) => (
              <div key={w.numKey} style={{ padding: 40, borderRight: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
                <div data-cpath-fixed={w.numKey} data-cms-neutral style={{ fontFamily: "var(--f-serif-en)", fontSize: 56, color: "var(--gold)", fontStyle: "italic", lineHeight: 1 }}>{t(w.numKey)}</div>
                <div style={{ fontFamily: "var(--f-serif-cn)", fontSize: 26, marginTop: 16, fontWeight: 500 }}>{t(w.titleKey)}</div>
                <p className="body-mute" style={{ marginTop: 20 }}>{t(w.bodyKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="section">
        <div className="container-wide">
          <SectionHead
            lang={lang}
            title={t('aux.t035')}
          />
          {/* 招聘职位列表用专门的「招聘信息」菜单维护，预览里不进入文案编辑，
              右键给出跳转提示 */}
          <div data-cms-redirect="jobs">
            <CareerRows jobs={jobs || []} lang={lang} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "120px 0", background: "var(--bg-0)" }}>
        <div className="container" style={{ textAlign: "center" }}>
          <h2 className="h-1">{t('aux.t040')}</h2>
          <p className="body-lg body-mute" style={{ marginTop: 20, maxWidth: 540, margin: "20px auto 36px" }}>
            {t('aux.t041')}
          </p>
          <a className="btn" href="mailto:hr@kexicapital.com" style={{ display: "inline-flex" }}>
            hr@kexicapital.com<span className="arr"></span>
          </a>
        </div>
      </section>
    </div>
  );
}

Object.assign(window, { NewsPage, NewsDetailPage, ContactPage, CareersPage });
